// src/screens/MyTeam.tsx
import { useEffect, useState, useMemo, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate, Link } from "react-router-dom";
import { LogoLoader } from "../components/LogoLoader";
import {
  Trash2,
  BarChart2,
  Pencil,
  CirclePlus,
  ChartBar,
  ArrowUp01,
  ArrowDown10,
  Shield,
  Archive,
  History,
  Building2,
  Loader2,
  Check,
  MessageCircle,
} from "lucide-react";
import i18n from "../i18n";
import TeamSettingsCollaborators from "../components/TeamSettingsCollaborators";

// --- mini hook i18n "safe" (évite TS2589 & force string) ---
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = useCallback(
    (key: string, options?: Record<string, unknown>) =>
      options != null
        ? String((i18n.t as any)(key, options))
        : String((i18n.t as any)(key)),
    []
  );
  return { t, lng };
}

type Player = {
  id: string;
  firstName: string;
  lastName: string;
  number: number;
  position: string;
  photoUrl?: string;
};

type TeamRow = {
  id: string;
  team_name: string;
  league?: string | null;
  players: Player[] | string | null; // peut être JSON string
  user_id: string; // owner id
  club_id?: string | null;
};

type EligibleClub = { id: string; name: string };

// ---- Rôles club habilités à rattacher une équipe (mêmes que ClubTeamCreate) ----
function clubRoleAllowsTeamManagement(dbRole: string): boolean {
  return ["dirigeant", "admin", "manager", "cadre_technique", "assistant"].includes(dbRole);
}

type MemberInfo = {
  role: "owner" | "coach" | "assistant" | "viewer";
  permissions: Record<string, boolean> | null;
  status: "active" | "invited" | "revoked";
};

type SortKey = "number" | "position";

// ---- Presets + helper permissions (identiques à TeamSettingsCollaborators) ----
const PRESETS = {
  owner: {
    edit_roster: true,
    edit_matches: true,
    edit_trainings: true,
    edit_plays: true,
    view_finance: true,
  },
  coach: {
    edit_roster: true,
    edit_matches: true,
    edit_trainings: true,
    edit_plays: true,
    view_finance: false,
  },
  assistant: {
    edit_roster: false,
    edit_matches: true,
    edit_trainings: true,
    edit_plays: true,
    view_finance: false,
  },
  viewer: {
    edit_roster: false,
    edit_matches: false,
    edit_trainings: false,
    edit_plays: false,
    view_finance: false,
  },
} as const;

type PermKey = keyof typeof PRESETS["viewer"];

function canDo(member: MemberInfo | null | undefined, key: PermKey) {
  if (!member) return false;
  if (member.role === "owner") return true;
  const src = member.permissions ?? PRESETS[member.role];
  return !!src?.[key];
}

// ---- UI: badge de rôle ----
const ROLE_STYLES: Record<MemberInfo["role"], string> = {
  owner:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  coach:
    "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  assistant:
    "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  viewer: "bg-gray-200 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
};

function RoleBadge({
  role,
  status,
}: {
  role: MemberInfo["role"];
  status: MemberInfo["status"];
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${ROLE_STYLES[role]}`}
      title={status === "invited" ? "Invitation en attente" : undefined}
    >
      {role}
      {status === "invited" && <span className="opacity-80">(invited)</span>}
    </span>
  );
}

// ---- util: normaliser players en Player[] ----
function toPlayerArray(players: TeamRow["players"]): Player[] {
  if (!players) return [];
  if (typeof players === "string") {
    try {
      const parsed = JSON.parse(players);
      return Array.isArray(parsed) ? (parsed as Player[]) : [];
    } catch {
      return [];
    }
  }
  return players as Player[];
}

export const MyTeam = () => {
  const { t } = useI18nSafe();
  const navigate = useNavigate();

  const [players, setPlayers] = useState<Player[]>([]);
  const [teamName, setTeamName] = useState<string>(t("myteam:default_team_name"));
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [selectedTeamIndex, setSelectedTeamIndex] = useState(0);
  const [isPremium, setIsPremium] = useState(false);

  // membership courant par teamId
  const [membershipByTeam, setMembershipByTeam] = useState<Record<string, MemberInfo>>({});
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // UI: afficher/masquer le module Collaborateurs
  const [showCollaborators, setShowCollaborators] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  // UI: lier l'équipe à un club
  const [showAttachClub, setShowAttachClub] = useState(false);
  const [eligibleClubs, setEligibleClubs] = useState<EligibleClub[]>([]);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [selectedClubId, setSelectedClubId] = useState<string>("");
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  // tri
  const [sortKey, setSortKey] = useState<SortKey>("number");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // ✅ Mobile detection (réactif)
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 640; // tailwind sm
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  // ordre pour les postes (FR/EN + abréviations)
  const getPosRank = (posRaw: string) => {
    const pos = (posRaw || "").toLowerCase().trim();
    if (pos === "pg") return 1;
    if (pos === "sg") return 2;
    if (pos === "sf") return 3;
    if (pos === "pf") return 4;
    if (pos === "c") return 5;
    if (pos === "g") return 2;
    if (pos === "f") return 3;

    if (pos.includes("meneur")) return 1;
    if (pos.includes("arri")) return 2;
    if (pos.includes("ailier fort")) return 4;
    if (pos === "ailier" || (pos.includes("ailier") && !pos.includes("fort"))) return 3;
    if (pos.includes("pivot")) return 5;

    if (pos.includes("point guard")) return 1;
    if (pos.includes("shooting guard")) return 2;
    if (pos.includes("small forward")) return 3;
    if (pos.includes("power forward")) return 4;
    if (pos.includes("center")) return 5;

    return 99;
  };

  const refreshSignedUrls = useCallback(async (team: TeamRow | null) => {
    if (!team) {
      setSignedUrls({});
      return;
    }
    const arr: Player[] = toPlayerArray(team.players);
    const photoPaths: string[] = arr
      .filter((p: Player) => !!p.photoUrl)
      .map((p: Player) => p.photoUrl!) as string[];

    if (photoPaths.length === 0) {
      setSignedUrls({});
      return;
    }

    const { data: urls } = await supabase.storage
      .from("player-photos")
      .createSignedUrls(photoPaths, 3600);
    const map: Record<string, string> = {};
    urls?.forEach((u) => {
      if (u.path && u.signedUrl) map[u.path] = u.signedUrl;
    });
    setSignedUrls(map);
  }, []);

  const displayedPlayers = useMemo(() => {
    const copy = [...players];
    copy.sort((a, b) => {
      if (sortKey === "number") {
        const av = Number.isFinite(a.number) ? a.number : 0;
        const bv = Number.isFinite(b.number) ? b.number : 0;
        return sortDir === "asc" ? av - bv : bv - av;
      } else {
        const ra = getPosRank(a.position);
        const rb = getPosRank(b.position);
        if (ra !== rb) return sortDir === "asc" ? ra - rb : rb - ra;
        const la = (a.position || "").toLowerCase();
        const lb = (b.position || "").toLowerCase();
        const cmp = la.localeCompare(lb, "fr");
        return sortDir === "asc" ? cmp : -cmp;
      }
    });
    return copy;
  }, [players, sortKey, sortDir]);

  // ---- Fetch Teams: owner + memberships (uid + invited_email) ----
  useEffect(() => {
    const fetchTeams = async () => {
      setLoading(true);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }
      setCurrentUserId(user.id);
      const userEmail = (user.email || "").toLowerCase();

      // Premium
      const { data: profile } = await supabase
        .from("user_profiles")
        .select("is_premium")
        .eq("id", user.id)
        .single();
      setIsPremium(profile?.is_premium ?? false);

      // 1) ▶︎ TOUTES les teams visibles via RLS (hors archivées)
      const { data: visibleTeams, error: teamsErr } = await supabase
        .from("teams")
        .select("id, team_name, league, players, user_id, club_id")
        .or("archived.is.null,archived.eq.false");

      if (teamsErr) {
        console.error("[MyTeam] teams fetch error:", teamsErr);
        setTeams([]);
        setMembershipByTeam({});
        setLoading(false);
        return;
      }

      // 2) ▶︎ Memberships par UID + par invited_email (user_id null)
      const [{ data: mUid, error: e1 }, { data: mEmail, error: e2 }] =
        await Promise.all([
          supabase
            .from("team_members")
            .select("team_id, role, permissions, status")
            .eq("user_id", user.id),
          supabase
            .from("team_members")
            .select("team_id, role, permissions, status")
            .is("user_id", null)
            .eq("invited_email", userEmail),
        ]);

      if (e1) console.warn("[MyTeam] team_members by uid warn:", e1);
      if (e2) console.warn("[MyTeam] team_members by invited_email warn:", e2);

      // 3) ▶︎ Construire la map des rôles
      const membershipMap: Record<string, MemberInfo> = {};
      (visibleTeams || []).forEach((t: TeamRow) => {
        if (t.user_id === user.id) {
          membershipMap[t.id] = {
            role: "owner",
            permissions: PRESETS.owner,
            status: "active",
          };
        }
      });

      [...(mUid || []), ...(mEmail || [])].forEach((m: any) => {
        membershipMap[m.team_id] = {
          role: m.role ?? "assistant",
          permissions: m.permissions ?? null,
          status: m.status ?? "active",
        };
      });

      // 4) ▶︎ Tri + init affichage
      const sorted = [...(visibleTeams || [])].sort((a, b) =>
        (a.team_name || "").localeCompare(b.team_name || "", "fr", {
          sensitivity: "base",
        })
      );

      setTeams(sorted);
      setMembershipByTeam(membershipMap);

      if (sorted.length > 0) {
        const first = sorted[0];
        setSelectedTeamIndex(0);
        setTeamName(first.team_name || t("myteam:default_team_name"));
        const safePlayers = toPlayerArray(first.players);
        setPlayers(safePlayers);
        await refreshSignedUrls({ ...first, players: safePlayers });
      } else {
        setPlayers([]);
        setSignedUrls({});
      }

      setLoading(false);
    };

    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignedUrls]);

  if (loading) {
    return <LogoLoader />;
  }

  const currentTeam = teams[selectedTeamIndex] ?? null;
  const currentMember: MemberInfo | null = currentTeam
    ? membershipByTeam[currentTeam.id] || null
    : null;
  const isOwner = currentTeam && currentUserId && currentTeam.user_id === currentUserId;
  const canEditRoster = canDo(currentMember, "edit_roster");

  const dirLabel = t(`myteam:${sortKey === "number" ? sortDir : "asc"}`);
  const dirLabelPos = t(`myteam:${sortKey === "position" ? sortDir : "asc"}`);

  const handleDeletePlayer = async (playerId: string) => {
    if (!canEditRoster) {
      alert(t("myteam:not_allowed_action"));
      return;
    }
    const confirmDelete = window.confirm(t("myteam:delete_player_confirm"));
    if (!confirmDelete) return;

    const team = teams[selectedTeamIndex];
    if (!team) return;

    const updatedPlayers = (players || []).filter((p) => p.id !== playerId);
    const removed = (players || []).find((p) => p.id === playerId);

    const { error } = await supabase
      .from("teams")
      .update({ players: updatedPlayers })
      .eq("id", team.id);

    if (!error) {
      setPlayers(updatedPlayers);
      await refreshSignedUrls({ ...team, players: updatedPlayers });
      if (removed)
        alert(
          t("myteam:delete_player_success", {
            firstName: removed.firstName,
            lastName: removed.lastName,
          })
        );
    } else {
      alert(t("myteam:delete_player_error"));
    }
  };

  const handleArchiveTeam = async () => {
    if (!isOwner) {
      alert(t("myteam:not_allowed_action"));
      return;
    }
    const confirmArchive = window.confirm(t("myteam:archive_team_confirm"));
    if (!confirmArchive) return;

    const team = teams[selectedTeamIndex];
    if (!team) return;

    setIsArchiving(true);
    try {
      // 1. Fetch matches
      const { data: matchesData } = await supabase
        .from("matches")
        .select("*")
        .eq("team_id", team.id);

      const matchIds = (matchesData || []).map((m: any) => m.id);

      // 2. Fetch stats + shots
      const [{ data: statsData }, { data: shotsData }] = await Promise.all([
        matchIds.length > 0
          ? supabase.from("match_stats").select("*").in("match_id", matchIds)
          : Promise.resolve({ data: [] }),
        matchIds.length > 0
          ? supabase.from("shot_locations").select("*").in("match_id", matchIds)
          : Promise.resolve({ data: [] }),
      ]);

      // 3. Build snapshot
      const snapshot = {
        team: { ...team, players: toPlayerArray(team.players) },
        matches: (matchesData || []).map((m: any) => ({
          ...m,
          stats: (statsData || []).filter((s: any) => s.match_id === m.id),
          shots: (shotsData || []).filter((s: any) => s.match_id === m.id),
        })),
      };

      // 4. Save snapshot to team_archives
      const { error: archiveError } = await supabase
        .from("team_archives")
        .insert({
          team_id: team.id,
          team_name: team.team_name,
          user_id: currentUserId,
          data: snapshot,
        });

      if (archiveError) {
        console.error("[Archive] insert error:", archiveError);
        alert(t("myteam:archive_team_error"));
        return;
      }

      // 5. Mark team as archived
      const { error: updateError } = await supabase
        .from("teams")
        .update({ archived: true })
        .eq("id", team.id);

      if (!updateError) {
        alert(t("myteam:archive_team_success"));
        window.location.href = "/myteam";
      } else {
        console.error("[Archive] update error:", updateError);
        alert(t("myteam:archive_team_error"));
      }
    } finally {
      setIsArchiving(false);
    }
  };

  const openAttachClub = async () => {
    setShowAttachClub(true);
    setAttachError(null);
    setSelectedClubId("");
    setLoadingClubs(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Utilisateur non connecté.");

      const { data: memberships, error: membershipsErr } = await supabase
        .from("club_members")
        .select("club_id, role")
        .eq("user_id", user.id)
        .eq("status", "active");

      if (membershipsErr) throw membershipsErr;

      const clubIds = [...new Set(
        (memberships || [])
          .filter((m: any) => m.club_id && clubRoleAllowsTeamManagement(m.role))
          .map((m: any) => m.club_id as string)
      )];

      if (clubIds.length === 0) {
        setEligibleClubs([]);
        return;
      }

      const { data: clubRows, error: clubsErr } = await supabase
        .from("clubs")
        .select("id, name")
        .in("id", clubIds);

      if (clubsErr) throw clubsErr;

      setEligibleClubs((clubRows || []).map((c: any) => ({ id: c.id, name: c.name || "Club" })));
    } catch (err: any) {
      setAttachError(err?.message || t("myteam:attach_to_club_error"));
    } finally {
      setLoadingClubs(false);
    }
  };

  const handleAttachClub = async () => {
    if (!currentTeam || !selectedClubId) return;
    setAttaching(true);
    setAttachError(null);
    try {
      const { error } = await supabase
        .from("teams")
        .update({ club_id: selectedClubId })
        .eq("id", currentTeam.id);

      if (error) throw error;

      setTeams((prev) =>
        prev.map((tm) => (tm.id === currentTeam.id ? { ...tm, club_id: selectedClubId } : tm))
      );
      setShowAttachClub(false);
      alert(t("myteam:attach_to_club_success"));
    } catch (err: any) {
      setAttachError(err?.message || t("myteam:attach_to_club_error"));
    } finally {
      setAttaching(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!isOwner) {
      alert(t("myteam:not_allowed_action"));
      return;
    }
    const confirmDelete = window.confirm(t("myteam:delete_team_confirm"));
    if (!confirmDelete) return;

    const team = teams[selectedTeamIndex];
    if (!team) return;

    // supprimer photos des joueurs
    const arr: Player[] = toPlayerArray(team.players);
    const photoPaths: string[] = arr
      .filter((p: Player) => !!p.photoUrl)
      .map((p: Player) => p.photoUrl as string);

    if (photoPaths.length > 0) {
      await supabase.storage.from("player-photos").remove(photoPaths);
    }

    // supprimer matches + stats + shots liés à l'équipe
    const { data: matches } = await supabase
      .from("matches")
      .select("id")
      .eq("team_id", team.id);

    const matchIds = matches?.map((m) => m.id) || [];

    if (matchIds.length > 0) {
      await supabase.from("shot_locations").delete().in("match_id", matchIds);
      await supabase.from("match_stats").delete().in("match_id", matchIds);
      await supabase.from("matches").delete().in("id", matchIds);
    }

    const { error: deleteError } = await supabase
      .from("teams")
      .delete()
      .eq("id", team.id);

    if (!deleteError) {
      alert(t("myteam:delete_team_success"));
      window.location.href = "/myteam";
    } else {
      alert(t("myteam:delete_team_error"));
    }
  };

  return (
    <section className="flex flex-col items-center p-6 min-h-screen bg-gray-50 dark:bg-gray-950 dark:text-gray-100">
      {/* Sélecteur d'équipe */}
      {teams.length > 0 && (
        <div className="mb-4 flex gap-4 items-center">
          <label
            htmlFor="team-select"
            className="font-semibold text-gray-800 dark:text-gray-200"
          >
            {t("myteam:team_label")}
          </label>
          <select
            id="team-select"
            value={selectedTeamIndex}
            onChange={async (e) => {
              const index = parseInt(e.target.value, 10);
              setSelectedTeamIndex(index);
              const team = teams[index];
              setTeamName(team.team_name || t("myteam:default_team_name"));
              const safePlayers = toPlayerArray(team.players);
              setPlayers(safePlayers);
              await refreshSignedUrls({ ...team, players: safePlayers });
              setShowCollaborators(false);
            }}
            className="border px-3 py-1 rounded bg-white text-gray-900 border-gray-300 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700"
          >
            {teams.map((trow, index) => {
              const m = membershipByTeam[trow.id];
              const myRole: MemberInfo["role"] | undefined =
                m?.role ?? (trow.user_id === currentUserId ? "owner" : undefined);
              const suffix = myRole
                ? ` — ${myRole}${m?.status === "invited" ? " (invited)" : ""}`
                : "";
              const label =
                (trow.team_name ||
                  t("myteam:team_option_fallback", { index: index + 1 })) + suffix;
              return (
                <option key={trow.id} value={index}>
                  {label}
                </option>
              );
            })}
          </select>
        </div>
      )}

      {/* Bouton créer équipe selon premium/limites (inchangé) */}
      {(isPremium && teams.length < 3) || (!isPremium && teams.length < 1) ? (
        <button
          onClick={() => navigate("/teams/new")}
          className="mb-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold"
        >
          {t("myteam:create_new_team")}
        </button>
      ) : (
        !isPremium &&
        teams.length >= 1 && (
          <p className="mb-4 text-sm text-gray-700 dark:text-gray-300">
            {t("myteam:free_limit_reached")}{" "}
            <Link
              to="/upgrade"
              className="text-orange-600 dark:text-orange-400 underline font-medium"
            >
              {t("myteam:go_premium")}
            </Link>{" "}
            {t("myteam:create_up_to_n_teams", { count: 3 })}
          </p>
        )
      )}

      {/* ==== Header équipe + actions ==== */}
      <div className="w-full max-w-2xl mb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 min-w-0">
          <div>
            <h1 className="text-2xl font-bold text-orange-500 dark:text-orange-400 truncate">
              {teamName}
            </h1>
            {currentTeam &&
              (() => {
                const m = membershipByTeam[currentTeam.id];
                const role: MemberInfo["role"] = m?.role ?? (isOwner ? "owner" : "viewer");
                const status: MemberInfo["status"] = m?.status ?? "active";
                return (
                  <div className="mt-1">
                    <RoleBadge role={role} status={status} />
                  </div>
                );
              })()}
          </div>

          {currentTeam && (
            <div className="flex items-center gap-3 flex-wrap">
              {/* Éditer équipe (owner OU coach/assistant avec edit_roster) */}
              {(isOwner || canEditRoster) && (
                <Link
                  to={`/editteam/${currentTeam.id}`}
                  className="p-2 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300"
                  title={t("myteam:edit_team_title")}
                >
                  <Pencil className="w-5 h-5" />
                  <span className="sr-only">{t("myteam:edit_team_title")}</span>
                </Link>
              )}

              {/* Ajouter joueur (need edit_roster) */}
              {canEditRoster && (
                <Link
                  to={`/addplayer/${currentTeam.id}`}
                  className="p-2 rounded-md bg-orange-50 text-orange-700 hover:bg-orange-100 dark:bg-orange-950/30 dark:text-orange-300"
                  title={t("myteam:add_player_title")}
                >
                  <CirclePlus className="w-5 h-5" />
                  <span className="sr-only">{t("myteam:add_player_title")}</span>
                </Link>
              )}

              <Link
                to={`/myteam/${currentTeam.id}/messages`}
                className="p-2 rounded-md bg-purple-50 text-purple-700 hover:bg-purple-100 dark:bg-purple-950/30 dark:text-purple-300"
                title={t("myteam:team_chat_title")}
              >
                <MessageCircle className="w-5 h-5" />
                <span className="sr-only">{t("myteam:team_chat_title")}</span>
              </Link>

              <Link
                to="/archived-teams"
                className="p-2 rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-950/30 dark:text-amber-300"
                title={t("myteam:archived_teams_title")}
              >
                <History className="w-5 h-5" />
                <span className="sr-only">{t("myteam:archived_teams_title")}</span>
              </Link>

              <Link
                to="/seasonstats"
                className="p-2 rounded-md bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-950/30 dark:text-green-300"
                title={t("myteam:season_stats_title")}
              >
                <BarChart2 className="w-5 h-5" />
                <span className="sr-only">{t("myteam:season_stats_title")}</span>
              </Link>

              <Link
                to="/teamstats"
                className="p-2 rounded-md bg-purple-50 text-purple-700 hover:bg-purple-100 dark:bg-purple-950/30 dark:text-purple-300"
                title={t("myteam:team_report_title")}
              >
                <ChartBar className="w-5 h-5" />
                <span className="sr-only">{t("myteam:team_report_title")}</span>
              </Link>

              {/* Collaborateurs (desktop uniquement) */}
              {isOwner && !isMobile && (
                <button
                  onClick={() => setShowCollaborators((s) => !s)}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                    showCollaborators
                      ? "bg-orange-500 text-white hover:bg-orange-600"
                      : "border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  <Shield className="w-4 h-4" />
                  {t("teams:collaborators")}
                </button>
              )}
            </div>
          )}
        </div>

        {/* ✅ Collaborateurs (mobile full-width) */}
        {currentTeam && isOwner && isMobile && (
          <div className="mt-3">
            <button
              onClick={() => setShowCollaborators((s) => !s)}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium active:scale-[0.98] transition-colors ${
                showCollaborators
                  ? "bg-orange-500 text-white"
                  : "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100"
              }`}
            >
              <Shield className="w-5 h-5" />
              {t("teams:collaborators")}
            </button>
          </div>
        )}

        {currentTeam?.league && (
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            {t("myteam:championship_label")} {currentTeam.league}
          </div>
        )}
      </div>

      {/* ==== Bloc Collaborateurs ==== */}
{currentTeam && isOwner && showCollaborators && (
  <>
    {/* ===== DESKTOP ===== */}
    {!isMobile && (
      <div className="w-full max-w-2xl mb-6">
        <TeamSettingsCollaborators
          teamId={currentTeam.id}
          frontendUrl={process.env.REACT_APP_FRONTEND_URL}
        />
      </div>
    )}

    {/* ===== MOBILE (MODALE SAFE iOS) ===== */}
    {isMobile && (
      <div className="fixed inset-0 z-50 flex items-end justify-center">
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-black/40"
          onClick={() => setShowCollaborators(false)}
        />

        {/* Modale */}
        <div
          className="
            relative z-10
            w-full
            bg-white dark:bg-gray-950
            rounded-t-2xl
            shadow-xl
            max-h-[85vh]
            overflow-hidden
          "
          style={{
            paddingTop: "env(safe-area-inset-top)",
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          {/* Header sticky */}
          <div className="
            sticky top-0 z-20
            flex items-center justify-between
            px-4 py-3
            border-b border-gray-200 dark:border-gray-800
            bg-white/90 dark:bg-gray-950/90
            backdrop-blur
          ">
            <h2 className="text-lg font-semibold">
              {t("teams:collaborators")}
            </h2>

            {/* ⚠️ PAS de fallback string ici */}
            <button
              onClick={() => setShowCollaborators(false)}
              className="text-sm px-3 py-1 rounded bg-gray-200 dark:bg-gray-800"
            >
              {t("common:close")}
            </button>
          </div>

          {/* Contenu scrollable */}
          <div className="overflow-y-auto px-4 py-4 max-h-[70vh]">
            <TeamSettingsCollaborators
              teamId={currentTeam.id}
              frontendUrl={process.env.REACT_APP_FRONTEND_URL}
            />
          </div>
        </div>
      </div>
    )}
  </>
)}



      {/* ==== Table joueurs ==== */}
      {players.length === 0 ? (
        <p className="text-gray-700 dark:text-gray-300">{t("myteam:no_players")}</p>
      ) : (
        <div className="w-full max-w-2xl">
          <div className="overflow-x-auto">
            <table className="w-full bg-white dark:bg-gray-900 shadow rounded-lg overflow-hidden">
              <thead className="bg-gray-200 dark:bg-gray-800">
                <tr>
                  <th className="p-3 text-left">{t("myteam:table.photo")}</th>
                  <th className="p-3 text-left">
                    <button
                      type="button"
                      onClick={() => {
                        if (sortKey === "number") {
                          setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                        } else {
                          setSortKey("number");
                          setSortDir("asc");
                        }
                      }}
                      className="inline-flex items-center gap-1 hover:underline"
                      title={t("myteam:sort_by_number", {
                        dir: sortKey === "number" ? dirLabel : t("myteam:asc"),
                      })}
                    >
                      {t("myteam:table.number")}
                      {sortKey === "number"
                        ? sortDir === "asc"
                          ? <ArrowUp01 className="w-4 h-4" />
                          : <ArrowDown10 className="w-4 h-4" />
                        : null}
                    </button>
                  </th>
                  <th className="p-3 text-left">{t("myteam:table.last_name")}</th>
                  <th className="p-3 text-left">{t("myteam:table.first_name")}</th>
                  <th className="p-3 text-left">
                    <button
                      type="button"
                      onClick={() => {
                        if (sortKey === "position") {
                          setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                        } else {
                          setSortKey("position");
                          setSortDir("asc");
                        }
                      }}
                      className="inline-flex items-center gap-1 hover:underline"
                      title={t("myteam:sort_by_position", {
                        dir: sortKey === "position" ? dirLabelPos : t("myteam:asc"),
                      })}
                    >
                      {t("myteam:table.position")}
                      {sortKey === "position"
                        ? sortDir === "asc"
                          ? <ArrowUp01 className="w-4 h-4" />
                          : <ArrowDown10 className="w-4 h-4" />
                        : null}
                    </button>
                  </th>
                  <th className="p-3 text-left">{t("myteam:table.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {displayedPlayers.map((player) => (
                  <tr
                    key={player.id}
                    className="border-b hover:bg-orange-600/10 dark:hover:bg-orange-500/10 cursor-pointer"
                    onClick={() => navigate(`/player/by-id/${player.id}`)}
                  >
                    <td className="p-3">
                      {player.photoUrl && signedUrls[player.photoUrl] ? (
                        <img
                          src={signedUrls[player.photoUrl]}
                          alt={`${player.firstName} ${player.lastName}`}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center text-sm text-white">
                          {t("myteam:unknown_avatar")}
                        </div>
                      )}
                    </td>
                    <td className="p-3">{player.number}</td>
                    <td className="p-3">{player.lastName}</td>
                    <td className="p-3">{player.firstName}</td>
                    <td className="p-3">{player.position}</td>
                    <td className="p-3">
                      {canEditRoster && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePlayer(player.id);
                          }}
                          className="text-red-600 hover:text-red-800"
                          title={t("myteam:delete_player_title")}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Actions owner : lier à un club + archiver + supprimer */}
      {currentTeam && isOwner && (
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          {!currentTeam.club_id && (
            <button
              onClick={openAttachClub}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-300 flex items-center gap-2"
            >
              <Building2 className="w-5 h-5" />
              {t("myteam:attach_to_club_button")}
            </button>
          )}

          <button
            onClick={handleArchiveTeam}
            disabled={isArchiving}
            className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-semibold py-3 px-6 rounded-lg transition duration-300 flex items-center gap-2"
          >
            <Archive className="w-5 h-5" />
            {isArchiving ? t("myteam:archive_team_loading") : t("myteam:archive_team_button")}
          </button>

          <button
            onClick={handleDeleteTeam}
            className="bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-300 flex items-center gap-2"
          >
            <Trash2 className="w-5 h-5" />
            {t("myteam:delete_team_button")}
          </button>
        </div>
      )}

      {/* ==== Modale : lier l'équipe à un club ==== */}
      {showAttachClub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAttachClub(false)} />
          <div className="relative z-10 w-full max-w-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <h4 className="text-base font-semibold flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-500" />
                {t("myteam:attach_to_club_title")}
              </h4>
              <button
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400"
                onClick={() => setShowAttachClub(false)}
              >
                ✕
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {attachError && (
                <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950/20 px-3 py-2 rounded-lg">
                  {attachError}
                </div>
              )}

              {loadingClubs ? (
                <div className="flex items-center gap-2 text-gray-500 py-4">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t("common:loading")}
                </div>
              ) : eligibleClubs.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("myteam:attach_to_club_none")}
                </p>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    {t("myteam:attach_to_club_select_label")}
                  </label>
                  <select
                    value={selectedClubId}
                    onChange={(e) => setSelectedClubId(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="">{t("myteam:attach_to_club_select_placeholder")}</option>
                    {eligibleClubs.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-800">
              <button
                onClick={() => setShowAttachClub(false)}
                className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                {t("common:cancel")}
              </button>
              <button
                onClick={handleAttachClub}
                disabled={attaching || !selectedClubId}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50 transition-colors"
              >
                {attaching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {t("myteam:attach_to_club_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
